"use client";

import { motion } from "framer-motion";
import { Autoplay, EffectCoverflow } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css";
import "swiper/css/effect-cards";

import Link from "next/link";
import { Button } from "@/components/ui/button";

// Caption examples showing all three length options with realistic word counts
const heroExamples = [
  {
    src: "/images/dog_caption_example.png",
    alt: "Dog with frisbee",
    length: "short",
    caption: "A brown dog holds a frisbee on a sidewalk next to yellow flowers.",
  },
  {
    src: "/images/nike.png",
    alt: "Nike Air Force 1 shoe",
    length: "normal",
    caption: "A white Nike Air Force 1 high-top sneaker photographed against a plain background. The shoe features the classic Air Force 1 silhouette with a perforated toe box, padded ankle collar, and the iconic Nike Swoosh logo on the side. The thick white rubber sole and clean leather upper give it a timeless streetwear aesthetic.",
  },
  {
    src: "/images/solutions/wildlife-detection/moose_example.jpg",
    alt: "Moose on trail camera",
    length: "short",
    caption: "A moose with large antlers walks on a dirt path in a forest.",
  },
  {
    src: "/images/busy_airport.jpeg",
    alt: "Airport terminal",
    length: "long",
    caption: "A bustling airport terminal interior captured from an elevated perspective, showing travelers navigating through a spacious concourse. The architecture features a dramatic vaulted ceiling with exposed structural beams and large glass panels allowing natural light to flood the space. Multiple departure gates line both sides of the terminal, with digital flight information displays visible above each gate. Travelers of various ages pull rolling luggage while others rest on rows of connected seating. The polished floor reflects the overhead lighting, creating a sense of depth and movement. In the background, retail shops and food vendors are visible along the terminal walls. The scene captures the organized chaos of modern air travel during what appears to be a peak travel period.",
  },
  {
    src: "/images/solutions/robot-vision/robot_dishes_example.jpg",
    alt: "Kitchen scene",
    length: "normal",
    caption: "A kitchen counter viewed from above showing a stainless steel sink containing dirty dishes. Various plates, bowls, and utensils are stacked in the sink basin. The surrounding countertop is light-colored with some water spots visible. A faucet with a chrome finish is positioned at the back of the sink.",
  },
];

// Duplicate for smooth looping with coverflow effect
const images = [...heroExamples, ...heroExamples];

const css = `
  .mySwiperHero210 {
    width: 100%;
    height: 480px;
    padding-bottom: 50px;
  }
  
  .mySwiperHero210 .swiper-slide {
    background: transparent;
    width: 520px;
    margin: 0 20px;
  }
  
  .mySwiperHero210 .swiper-slide img {
    display: block;
    width: 100%;
  }
  
  .swiper-3d .swiper-slide-shadow-left,
  .swiper-3d .swiper-slide-shadow-right {
    display: none !important;
    background: none !important;
    background-image: none !important;
  }
  
`;

export default function CaptionHero() {
  return (
    <section className="pt-8 sm:pt-20">
      <style>{css}</style>
      <div className="container mx-auto px-0 sm:px-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-4 px-8 sm:px-0">
          <h1
            className="text-center text-5xl lg:text-7xl tracking-[-0.01em]"
            style={{ fontFamily: 'var(--font-instrument-serif)' }}
          >
            Accurate Captions, Fully Automated, at Any Scale
          </h1>
          <p className="text-muted-foreground text-center lg:text-lg">
            Generate rich descriptions for images and video frames. Choose from short, normal, or long captions to match your use case—from quick labels to detailed descriptions.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-full px-6 py-3">
              <Link href="/c/playground?skill=caption">Try Caption</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="rounded-full px-6 py-3"
            >
              <a
                href="https://docs.moondream.ai/skills/caption"
                target="_blank"
                rel="noreferrer"
              >
                View docs
              </a>
            </Button>
          </div>
        </div>

        <div className="relative mt-12 h-[480px] w-full lg:px-20">
          <div className="absolute left-0 z-10 h-full w-16 md:w-24 bg-gradient-to-r from-background to-transparent lg:left-20" />
          <div className="absolute right-0 z-10 h-full w-16 md:w-24 bg-gradient-to-l from-background to-transparent lg:right-20" />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <Swiper
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              effect="coverflow"
              grabCursor
              slidesPerView="auto"
              centeredSlides
              loop
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: false,
              }}
              className="mySwiperHero210"
              modules={[EffectCoverflow, Autoplay]}
            >
              {images.map((example, index) => (
                <SwiperSlide key={index} className="!bg-transparent">
                  <div className="relative h-full w-full overflow-hidden rounded-3xl shadow-lg">
                    <Image
                      className="h-full w-full object-cover"
                      src={example.src}
                      alt={example.alt}
                      width={520}
                      height={480}
                    />
                    
                    {/* Bottom gradient */}
                    <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
                    
                    {/* Caption overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white/15 backdrop-blur-md rounded-lg border border-white/25 px-4 py-3 shadow-lg">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-amber-300 text-xs font-semibold uppercase tracking-wider">Caption</span>
                          <span className="text-white/50 text-[10px] uppercase tracking-wider">• {example.length}</span>
                        </div>
                        <p className="text-white font-medium text-sm leading-relaxed line-clamp-3">
                          {example.caption}
                        </p>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </div>
    </section >
  );
}
